import React, { useEffect, useRef } from 'react';
import './../components.css';

// Homepage carousel: items auto-run (marquee) continuously and loop seamlessly (the list is
// rendered twice; the track is translated and wraps at the halfway mark). This uses a CSS
// transform rather than native scroll, so it runs regardless of how wide the screen is — even
// when the items would otherwise fit without overflowing. Auto-run pauses while the pointer is
// over the carousel (or during a touch drag); the ‹ / › arrows and touch-drag let the user go
// back to items that already passed by. Falls back to a plain centered row for very few items.
export default function Carousel({ children, minToScroll = 2, speed = 0.5 }) {
    const items = React.Children.toArray(children);
    const trackRef = useRef(null);
    const offsetRef = useRef(0);
    const pausedRef = useRef(false);
    const dragRef = useRef(null); // { startX, startOffset } while touch-dragging
    const scrollable = items.length >= minToScroll;

    // Width of a single copy of the list (half the duplicated track).
    const halfWidth = () => {
        const t = trackRef.current;
        return t ? t.scrollWidth / 2 : 0;
    };

    const apply = (offset, animate) => {
        const t = trackRef.current;
        if (!t) return;
        t.style.transition = animate ? 'transform 0.35s ease' : '';
        t.style.transform = `translateX(${-offset}px)`;
    };

    // Keep an offset within [0, halfWidth) so the two copies loop seamlessly.
    const wrap = (offset) => {
        const half = halfWidth();
        if (half <= 0) return 0;
        let o = offset;
        while (o < 0) o += half;
        while (o >= half) o -= half;
        return o;
    };

    useEffect(() => {
        if (!scrollable) return;
        let raf;
        const step = () => {
            if (!pausedRef.current) {
                offsetRef.current = wrap(offsetRef.current + speed);
                apply(offsetRef.current, false);
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollable, speed, items.length]);

    const nudge = (dir) => {
        const before = offsetRef.current;
        const target = before + dir * 320;
        const wrapped = wrap(target);
        // Animate only when we didn't cross the loop seam (avoids a long ugly sweep back).
        const crossed = Math.abs(wrapped - target) > 0.5;
        offsetRef.current = wrapped;
        apply(wrapped, !crossed);
    };

    const onTouchStart = (e) => {
        pausedRef.current = true;
        dragRef.current = { startX: e.touches[0].clientX, startOffset: offsetRef.current };
    };
    const onTouchMove = (e) => {
        if (!dragRef.current) return;
        const delta = dragRef.current.startX - e.touches[0].clientX;
        offsetRef.current = wrap(dragRef.current.startOffset + delta);
        apply(offsetRef.current, false);
    };
    const onTouchEnd = () => {
        dragRef.current = null;
        pausedRef.current = false;
    };

    if (items.length === 0) return null;

    if (!scrollable) {
        return (
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                {items}
            </div>
        );
    }

    return (
        <div
            className="kp-carousel"
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <button
                type="button"
                className="kp-carousel-arrow kp-carousel-arrow-left"
                onClick={() => nudge(-1)}
                aria-label="Scroll left"
            >
                ‹
            </button>
            <div className="kp-carousel-viewport">
                <div className="kp-carousel-track" ref={trackRef}>
                    {/* Rendered twice for a seamless loop. */}
                    {items.map((c, i) => <div key={`a-${i}`} className="kp-carousel-item">{c}</div>)}
                    {items.map((c, i) => <div key={`b-${i}`} className="kp-carousel-item" aria-hidden="true">{c}</div>)}
                </div>
            </div>
            <button
                type="button"
                className="kp-carousel-arrow kp-carousel-arrow-right"
                onClick={() => nudge(1)}
                aria-label="Scroll right"
            >
                ›
            </button>
        </div>
    );
}
